import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import Login from './components/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './components/Dashboard';
import PreApprovals from './components/PreApprovals';
import ProjectListings from './components/ProjectListings';
import BidWonSuccess from './components/BidWonSuccess';
import CFODashboard from './components/CFODashboard';
import TradePartnerDashboard from './components/TradePartnerDashboard';
import { RequirePremiumFeature } from './components/auth/RequirePremiumFeature';
import { CfoInsightsDashboard } from './components/cfo-insights/CfoInsightsDashboard';
import { CfoInsightsUpgrade } from './components/cfo-insights/CfoInsightsUpgrade';
import ScenariosPage from './components/cfo-insights/scenarios/ScenariosPage';
import ContractorBondReadyRequestsPage from './components/cfo-insights/ContractorBondReadyRequestsPage';
import GcRoiDashboard from './components/gc-roi/GcRoiDashboard';
import BondReadyOpportunity, { BondReadyEntry } from './components/gc-roi/BondReadyOpportunity';
import {
  createLocalE2EUser,
  isLocalE2EAuthEnabled,
  setLocalE2EUid,
} from './localE2eAuth';
import { consumeAuthReturnPath } from './routing/authReturnPath';

function LoadingRoute() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000000' }}>
      <div style={{ color: 'white' }}>Loading...</div>
    </div>
  );
}

function LoginRoute({ user, routeLoading, defaultRoute }) {
  const location = useLocation();
  if (!user) return <Login />;
  if (routeLoading) return <LoadingRoute />;
  const returnPath = consumeAuthReturnPath(location.state?.from);
  return <Navigate to={returnPath || defaultRoute} replace />;
}

function BondReadyAuthenticatedRoute({ user, featureKey, children }) {
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <RequirePremiumFeature featureKey={featureKey}>{children}</RequirePremiumFeature>;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultRoute, setDefaultRoute] = useState('/dashboard');
  const [routeLoading, setRouteLoading] = useState(true);

  useEffect(() => {
    if (isLocalE2EAuthEnabled()) {
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const nextUid = params.get('localE2eUid');
        if (nextUid) setLocalE2EUid(nextUid);
      }
      const localUser = createLocalE2EUser();
      setUser(localUser);
      setDefaultRoute('/cfo-insights/scenarios');
      setRouteLoading(false);
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);

      if (user) {
        setRouteLoading(true);
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists() && userDoc.data().defaultRoute) {
            setDefaultRoute(userDoc.data().defaultRoute);
          } else {
            setDefaultRoute('/dashboard');
          }
        } catch (error) {
          console.error('Error reading user defaultRoute:', error);
          setDefaultRoute('/dashboard');
        }
        setRouteLoading(false);
      } else {
        setDefaultRoute('/dashboard');
        setRouteLoading(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Allow switching local E2E identity via query (?localE2eUid=...) for partner deep-link checks.
  useEffect(() => {
    if (!isLocalE2EAuthEnabled() || typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    const nextUid = params.get('localE2eUid');
    if (!nextUid) return undefined;
    setLocalE2EUid(nextUid);
    const nextUser = createLocalE2EUser(nextUid);
    setUser(nextUser);
    return undefined;
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000000' }}>
        <div style={{ color: 'white' }}>Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={<LoginRoute user={user} routeLoading={routeLoading} defaultRoute={defaultRoute} />}
        />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/dashboard"
          element={
            user ? (
              <RequirePremiumFeature
                featureKey="dashboard"
                allowDefaultRoutes={["/dashboard"]}
              >
                <Dashboard />
              </RequirePremiumFeature>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/pre-approvals"
          element={user ? <PreApprovals /> : <Navigate to="/login" />}
        />
        <Route
          path="/project-listings"
          element={user ? <ProjectListings /> : <Navigate to="/login" />}
        />
        <Route
          path="/bid-won-success"
          element={user ? <BidWonSuccess /> : <Navigate to="/login" />}
        />
        <Route
          path="/cfo-dashboard"
          element={user ? <CFODashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/cfo/trade-partner"
          element={user ? <TradePartnerDashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/cfo-insights"
          element={user ? <CfoInsightsDashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/cfo-insights/scenarios"
          element={user ? <ScenariosPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/cfo-insights/bondready"
          element={user ? <ContractorBondReadyRequestsPage /> : <Navigate to="/login" />}
        />
        <Route path="/cfo-insights/upgrade" element={<CfoInsightsUpgrade />} />
        <Route
          path="/gc-roi"
          element={
            user ? (
              <RequirePremiumFeature featureKey="gc_roi">
                <GcRoiDashboard />
              </RequirePremiumFeature>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/bondready"
          element={
            <BondReadyAuthenticatedRoute user={user} featureKey="bondready">
              <BondReadyEntry />
            </BondReadyAuthenticatedRoute>
          }
        />
        <Route
          path="/bondready/:opportunityId"
          element={
            <BondReadyAuthenticatedRoute user={user} featureKey="bondready">
              <BondReadyOpportunity />
            </BondReadyAuthenticatedRoute>
          }
        />
        <Route
          path="/gc-roi/bondready/:opportunityId"
          element={
            <BondReadyAuthenticatedRoute user={user} featureKey="gc_roi">
              <BondReadyOpportunity />
            </BondReadyAuthenticatedRoute>
          }
        />
        <Route
          path="*"
          element={
            <div style={{ padding: '20px', color: 'white', backgroundColor: '#1e293b' }}>
              <h1>404 - Page Not Found</h1>
              <p>Route not found. Available routes:</p>
              <ul>
                <li>/login</li>
                <li>/dashboard</li>
                <li>/cfo-insights</li>
                <li>/cfo-insights/upgrade</li>
                <li>/cfo-insights/bondready</li>
                <li>/bondready</li>
              </ul>
            </div>
          }
        />
        <Route
          path="/"
          element={
            user ? (
              routeLoading ? (
                <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000000' }}>
                  <div style={{ color: 'white' }}>Loading...</div>
                </div>
              ) : (
                <Navigate to={defaultRoute} />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
